import React from 'react';
import { motion } from 'framer-motion';
import { QrCode, UserPlus, Target, Swords, Gift, Smartphone } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { DEMO_URL } from '../constants';

const stepIcons = [QrCode, UserPlus, Target, Swords, Gift];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, ease: 'easeOut', delay },
});

export default function HowItWorks({ onDemoClick }) {
  const { t } = useLanguage();
  const h = t.howItWorks;

  return (
    <section id="como-funciona" className="py-14 md:py-20 bg-[#1A222B]" data-testid="how-it-works-section">
      <div className="section-container">
        <motion.div className="text-center mb-4" {...fadeUp()}>
          <h2 className="heading-lg mb-3">{h.title}</h2>
          <p className="text-[#8A9099] text-base md:text-lg max-w-xl mx-auto">{h.subtitle}</p>
        </motion.div>

        {/* No app note */}
        <motion.div className="flex justify-center mb-14" {...fadeUp(0.1)}>
          <div className="flex items-center gap-2 bg-[#0F6B49]/15 border border-[#0F6B49]/30 rounded-full px-5 py-2.5">
            <Smartphone size={15} className="text-[#D5A63B]" />
            <span className="text-[#D5A63B] text-sm font-semibold">{h.noApp}</span>
          </div>
        </motion.div>

        {/* Steps */}
        <div className="relative">
          {/* Connector line (desktop) */}
          <div className="hidden lg:block absolute top-10 left-[10%] right-[10%] h-px bg-gradient-to-r from-transparent via-[#0F6B49]/40 to-transparent" />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {h.steps.map((step, i) => {
              const Icon = stepIcons[i];
              return (
                <motion.div key={i} className="relative flex flex-col items-center text-center"
                  {...fadeUp(0.1 + i * 0.1)} data-testid={`step-${i + 1}`}>
                  <div className="relative z-10 w-20 h-20 rounded-full bg-[#171E26] border-2 border-[#0F6B49]/50
                    flex flex-col items-center justify-center mb-5 shadow-lg
                    hover:border-[#D5A63B]/60 hover:shadow-[0_0_20px_rgba(213,166,59,0.2)] transition-all duration-300">
                    <span className="text-[#D5A63B] text-xs font-black font-exo">{step.num}</span>
                    <Icon size={18} className="text-[#0F6B49] mt-0.5" />
                  </div>
                  <h3 className="text-[#F3F1EB] font-bold text-base mb-2 font-exo italic">{step.title}</h3>
                  <p className="text-[#8A9099] text-sm leading-relaxed">{step.text}</p>
                </motion.div>
              );
            })}
          </div>
        </div>

        <motion.div className="text-center mt-14" {...fadeUp(0.6)}>
          <a href={DEMO_URL} target="_blank" rel="noopener noreferrer"
            className="btn-primary text-base px-8 py-3.5 no-underline" data-testid="how-cta-btn">
            {t.nav.requestDemo}
          </a>
        </motion.div>
      </div>
    </section>
  );
}
